import { Injectable, Inject } from '@angular/core';
import { Router, Resolve } from '@angular/router';
import { Observable, of } from 'rxjs';
import { mergeMap, take, map, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { AffiliateService } from '@shared/services/affiliate/affiliate.service';
import { AffiliateOption } from '@shared/services/affiliate/_interfaces_/affiliate-options.interface';
import { OverviewPage } from './_interfaces_/overview.interface';
import { AppState } from '@store/state';
import { UrlService } from '@shared/services/url/url.service';
import { AuthService } from '@shared/services/auth/auth.service';
import * as fullscreenLoaderActions from '@store/fullscreen-loader/fullscreen-loader.actions';
import { AnalyticsService } from '@shared/services/analytics/analytics.service';

@Injectable({
  providedIn: 'root',
})
export class OverviewResolver implements Resolve<OverviewPage> {
  constructor(
    @Inject(AnalyticsService) private _analyticsService: AnalyticsService,
    @Inject(AffiliateService) private _affiliateService: AffiliateService,
    @Inject(Router) private _router: Router,
    @Inject(UrlService) private _urlService: UrlService,
    @Inject(Store) private _store: Store<AppState>,
    @Inject(AuthService) private _auth: AuthService
  ) {}

  resolve(): Observable<OverviewPage> {
    const state = this._router.getCurrentNavigation().extras?.state;

    if (state?.authCode && state?.authState) {
      return this.authUser(state.authState, state.authCode);
    } else if (state?.bearerToken) {
      return this.retrieveRedirectedUserSession(state.bearerToken, state.selectedAffiliate, state.cultureCode);
    } else {
      return of({ showAffiliateOptionsModal: false });
    }
  }

  authUser(authState: string, authCode: string): Observable<OverviewPage> {
    this._store.dispatch(fullscreenLoaderActions.setVisibility({ isVisible: true }));
    return this._auth.authenticateUser(authState, authCode).pipe(
      tap(() => {
        this._urlService.setParsedUrls();
      }),
      mergeMap(() => this._affiliateService.getAffiliateOptions()),
      map((data: AffiliateOption[]) => ({ showAffiliateOptionsModal: data?.length > 1 })),
      mergeMap((overviewPage: OverviewPage) => this._analyticsService.sendLoginAnalytics().pipe(map(() => overviewPage))),
      take(1)
    );
  }

  retrieveRedirectedUserSession(bearerToken: string, selectedAffiliate: string, cultureCode: string): Observable<OverviewPage> {
    this._store.dispatch(fullscreenLoaderActions.setVisibility({ isVisible: true }));
    return this._auth.retrieveRedirectedUserSession({ bearerToken, selectedAffiliate, cultureCode }).pipe(
      tap(() => this._urlService.setParsedUrls()),
      mergeMap(() => this._affiliateService.getAffiliateOptions()),
      map((data: AffiliateOption[]) => ({ showAffiliateOptionsModal: data?.length > 1 })),
      take(1)
    );
  }
}
